import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { catalogApi, matterApi, http, type MatterType, type Court } from '../../api';
import Select from '../../components/common/Select';

// Stage shape returned by /catalog/procedural-stages (matter_type_id query).
type Stage = {
  ROWID: number;
  code: string;
  name: string;
  sequence: number;        // used internally only; never rendered
  description?: string;
  is_optional?: string | boolean;
  is_terminal?: string | boolean;
};

// Form state shape — kept as a single object so the validation effect can
// reason about every field together.
type FormState = {
  reference: string;
  matter_type_id: string;
  nature_description: string;
  court_id: string;
  court_freeform: string;
  case_number: string;
  opened_at: string;
  notes: string;
  starting_stage_id: string;
  seed_starting_stage_tasks: boolean;
  mc_jurisdiction: '' | 'district' | 'regional';
};

const TODAY = new Date().toISOString().slice(0, 10);

const BLANK_FORM: FormState = {
  reference: '',
  matter_type_id: '',
  nature_description: '',
  court_id: '',
  court_freeform: '',
  case_number: '',
  opened_at: TODAY,
  notes: '',
  starting_stage_id: '',
  seed_starting_stage_tasks: false,
  mc_jurisdiction: '',
};

const asBool = (v: unknown) => v === true || v === 'true';

export default function MatterNew() {
  const nav = useNavigate();
  const [matterTypes, setMatterTypes] = useState<MatterType[]>([]);
  const [courts, setCourts] = useState<Court[]>([]);
  const [stages, setStages] = useState<Stage[]>([]);
  const [form, setForm] = useState<FormState>(BLANK_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Catalog loads up front — both endpoints are cheap and tenant-scoped.
  useEffect(() => {
    catalogApi.matterTypes().then((r) => setMatterTypes(r.matter_types)).catch(() => null);
    catalogApi.courts().then((r) => setCourts(r.courts)).catch(() => null);
  }, []);

  // Stage list refetches when the matter type changes. Empty until type chosen.
  useEffect(() => {
    if (!form.matter_type_id) { setStages([]); return; }
    http.get<{ stages: Stage[] }>(`/catalog/procedural-stages?matter_type_id=${form.matter_type_id}`)
      .then((r) => setStages(r.stages || []))
      .catch(() => setStages([]));
    // Reset stage + ambit when the type changes — they're type-dependent.
    setForm((f) => ({ ...f, starting_stage_id: '', mc_jurisdiction: '' }));
  }, [form.matter_type_id]);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  // Selected court (resolved from id); null while none.
  const selectedCourt = useMemo(
    () => courts.find((c) => String(c.ROWID) === form.court_id) || null,
    [courts, form.court_id],
  );
  const isMagistrateCourt = selectedCourt?.tier === 'magistrate_court';
  const isAdHoc = form.court_id === '__freeform__';

  // All active courts feed straight into the picker — the user wanted the
  // full list grouped by category rather than filter chips. The EQC / SCC
  // designation is still surfaced inline in the row label so the advocate
  // can see at a glance which seat offers which function.

  // Validation — surfaces inline help. Submit is enabled only when valid.
  const errors = useMemo(() => {
    const e: Partial<Record<keyof FormState, string>> = {};
    if (!form.reference.trim()) e.reference = 'A matter reference is required.';
    else if (form.reference.length > 64) e.reference = 'Reference must be 64 characters or less.';
    if (!form.matter_type_id) e.matter_type_id = 'Pick the matter type so parties can be labelled correctly.';
    if (form.opened_at && form.opened_at > TODAY) e.opened_at = 'Opened-on cannot be in the future.';
    if (isAdHoc && !form.court_freeform.trim()) {
      e.court_freeform = 'Describe the ad-hoc forum (tribunal name + venue).';
    }
    if (isMagistrateCourt && !form.mc_jurisdiction) {
      e.mc_jurisdiction = 'Select District or Regional ambit — drives quantum and procedural rules.';
    }
    return e;
  }, [form, isAdHoc, isMagistrateCourt]);
  const isValid = Object.keys(errors).length === 0;

  const submit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!isValid || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      // IMPORTANT: keep IDs as digit strings — Catalyst ROWIDs are 17-19
      // digit BIGINTs that exceed JS safe-integer precision. Number() would
      // silently round the trailing digits.
      const r = await matterApi.create({
        reference: form.reference.trim(),
        matter_type_id: form.matter_type_id,
        court_id: isAdHoc ? undefined : (form.court_id || undefined),
        court_freeform: isAdHoc ? form.court_freeform.trim() : undefined,
        case_number: form.case_number.trim() || undefined,
        opened_at: form.opened_at,
        notes: form.notes.trim() || undefined,
        nature_description: form.nature_description.trim() || undefined,
        starting_stage_id: form.starting_stage_id || undefined,
        seed_starting_stage_tasks: form.seed_starting_stage_tasks || undefined,
        mc_jurisdiction: (isMagistrateCourt && (form.mc_jurisdiction === 'district' || form.mc_jurisdiction === 'regional'))
          ? form.mc_jurisdiction : undefined,
      });
      nav(`/matters/${r.id}`);
    } catch (err) {
      setError((err as Error).message);
      setSubmitting(false);
    }
  };

  return (
    <div style={{ maxWidth: 720, margin: '0 auto' }} className="stack">
      <div>
        <h1 style={{ marginBottom: 4 }}>New matter</h1>
        <p className="muted" style={{ fontSize: 13 }}>
          Create a matter file. You can leave most fields blank now and fill them in later — only the reference and matter type are required.
        </p>
      </div>

      <form className="card stack" style={{ gap: 0 }} onSubmit={submit}>
        {/* ───────────────── Identification ───────────────── */}
        <section className="form-section">
          <h3 className="form-section-title">Identification</h3>

          <div>
            <label htmlFor="reference">Matter reference</label>
            <input
              id="reference"
              required
              autoFocus
              value={form.reference}
              placeholder="e.g. ABC/2026/001"
              onChange={(e) => update('reference', e.target.value)}
            />
            <div className={`field-help ${errors.reference ? 'field-help-error' : ''}`}>
              {errors.reference || 'Your internal matter code. Used on invoices and quick-search.'}
            </div>
          </div>

          <div style={{ marginTop: 14 }}>
            <label htmlFor="matter_type">Matter type</label>
            <Select
              required
              value={form.matter_type_id}
              onChange={(v) => update('matter_type_id', v)}
              placeholder="— pick one —"
              searchPlaceholder="Search matter types…"
              options={matterTypes.map((t) => ({ value: String(t.ROWID), label: t.name, hint: t.code }))}
            />
            <div className={`field-help ${errors.matter_type_id ? 'field-help-error' : ''}`}>
              {errors.matter_type_id || 'Drives party labels (e.g. Plaintiff/Defendant, Applicant/Respondent), the procedural roadmap, and which deadline rules apply.'}
            </div>
          </div>

          <div style={{ marginTop: 14 }}>
            <label htmlFor="nature">Nature / brief description <span className="muted" style={{ textTransform: 'none', fontWeight: 400, letterSpacing: 0 }}>(optional)</span></label>
            <input
              id="nature"
              value={form.nature_description}
              placeholder='e.g. "review of granting liquor licence", "divorce with 2 minor children"'
              onChange={(e) => update('nature_description', e.target.value)}
            />
            <div className="field-help">
              Free-text label that shows under the matter name on the dashboard so you can recognise it at a glance.
            </div>
          </div>
        </section>

        {/* ───────────────── Forum ───────────────── */}
        <section className="form-section">
          <h3 className="form-section-title">Forum &amp; case</h3>
          <div>
            <label htmlFor="court">Court / forum</label>
            <Select
              value={form.court_id}
              onChange={(v) => update('court_id', v)}
              placeholder="— skip for now —"
              searchPlaceholder="Search by name, district, or seat…"
              options={[
                { value: '', label: '— skip for now —' },
                { value: '__freeform__', label: 'Other / ad-hoc forum (specify below)' },
                ...courts.map((c) => ({
                  value: String(c.ROWID),
                  label: courtPickerLabel(c),
                  group: courtGroupLabel(c),
                })),
              ]}
            />
            <div className="field-help">
              Optional at creation — you can pick the forum later. Required by the engine before you can advance past the Notice of Intention to Defend / Oppose stage.
            </div>

            {isAdHoc && (
              <div style={{ marginTop: 10 }}>
                <input
                  value={form.court_freeform}
                  placeholder='e.g. "Telkom Disciplinary Tribunal — chambers, Centurion"'
                  onChange={(e) => update('court_freeform', e.target.value)}
                />
                <div className={`field-help ${errors.court_freeform ? 'field-help-error' : ''}`}>
                  {errors.court_freeform || 'Ad-hoc forums have no Uniform / Magistrates\' Court rules attached, so the deadline engine is disabled. Add a recognised court later to enable rule-driven reminders.'}
                </div>
              </div>
            )}

            {/* Magistrates' Court ambit — chunky option cards. Same physical court
                hears District and Regional matters; quantum / criminal severity drives
                the choice. */}
            {isMagistrateCourt && (
              <div style={{ marginTop: 14 }}>
                <label>Magistrates' Court ambit</label>
                <div className="option-card-row">
                  <button
                    type="button"
                    className={`option-card ${form.mc_jurisdiction === 'district' ? 'selected' : ''}`}
                    onClick={() => update('mc_jurisdiction', 'district')}
                    aria-pressed={form.mc_jurisdiction === 'district'}
                  >
                    <input
                      type="radio"
                      name="mc_jurisdiction"
                      value="district"
                      checked={form.mc_jurisdiction === 'district'}
                      onChange={() => update('mc_jurisdiction', 'district')}
                      style={{ marginTop: 2 }}
                    />
                    <span>
                      <span className="option-card-title">District Court</span>
                      <span className="option-card-hint">Civil ≤ R200 000 · Criminal up to 3 years</span>
                    </span>
                  </button>
                  <button
                    type="button"
                    className={`option-card ${form.mc_jurisdiction === 'regional' ? 'selected' : ''}`}
                    onClick={() => update('mc_jurisdiction', 'regional')}
                    aria-pressed={form.mc_jurisdiction === 'regional'}
                  >
                    <input
                      type="radio"
                      name="mc_jurisdiction"
                      value="regional"
                      checked={form.mc_jurisdiction === 'regional'}
                      onChange={() => update('mc_jurisdiction', 'regional')}
                      style={{ marginTop: 2 }}
                    />
                    <span>
                      <span className="option-card-title">Regional Court</span>
                      <span className="option-card-hint">Civil R200k–R400k · Divorce · Serious criminal</span>
                    </span>
                  </button>
                </div>
                <div className={`field-help ${errors.mc_jurisdiction ? 'field-help-error' : ''}`}>
                  {errors.mc_jurisdiction || 'A regional magistrate sits in the same physical court as the district magistrate; the ambit drives quantum and procedural rules.'}
                </div>
              </div>
            )}
          </div>

          <div className="row" style={{ marginTop: 14, gap: 14 }}>
            <div className="grow">
              <label htmlFor="case_no">Case number <span className="muted" style={{ textTransform: 'none', fontWeight: 400, letterSpacing: 0 }}>(optional)</span></label>
              <input
                id="case_no"
                value={form.case_number}
                placeholder="e.g. 12345/2026"
                onChange={(e) => update('case_number', e.target.value)}
              />
            </div>
            <div style={{ width: 200 }}>
              <label htmlFor="opened_at">Opened on</label>
              <input
                id="opened_at"
                type="date"
                max={TODAY}
                value={form.opened_at}
                onChange={(e) => update('opened_at', e.target.value)}
              />
            </div>
          </div>
          {errors.opened_at && <div className="field-help field-help-error">{errors.opened_at}</div>}
        </section>

        {/* ───────────────── Stage entry ───────────────── */}
        {stages.length > 0 && (
          <section className="form-section">
            <h3 className="form-section-title">Where are you in this process?</h3>
            <div className="field-help" style={{ marginTop: 0, marginBottom: 10 }}>
              Default: start at the beginning. If you've been instructed mid-flow, pick the stage you're currently on — earlier stages are recorded as fast-forwarded so the procedural history is honest.
            </div>

            <Select
              value={form.starting_stage_id}
              onChange={(v) => update('starting_stage_id', v)}
              placeholder={`Default — start at "${stages[0]?.name || 'first stage'}"`}
              options={[
                { value: '', label: `Default — start at "${stages[0]?.name || 'first stage'}"` },
                // Sequence numbers are deliberately omitted — the order is
                // already implicit in the dropdown order.
                ...stages.map((s) => ({
                  value: String(s.ROWID),
                  label: s.name,
                  hint: s.is_optional ? 'optional stage' : undefined,
                })),
              ]}
            />

            {form.starting_stage_id && (
              <div style={{ marginTop: 12 }}>
                <label className="option-card" style={{ alignItems: 'flex-start' }}>
                  <input
                    type="checkbox"
                    checked={form.seed_starting_stage_tasks}
                    onChange={(e) => update('seed_starting_stage_tasks', e.target.checked)}
                    style={{ marginTop: 3 }}
                  />
                  <span>
                    <span className="option-card-title">Also create the standard to-do list for this stage</span>
                    <span className="option-card-hint">
                      Default is off — most mid-flow briefs arrive with the stage already in progress, so seeding the default tasks just duplicates work that's been done.
                      Tick this if you want the full procedural checklist for the current stage to start fresh.
                    </span>
                  </span>
                </label>
              </div>
            )}
          </section>
        )}

        {/* ───────────────── Notes ───────────────── */}
        <section className="form-section">
          <h3 className="form-section-title">Notes</h3>
          <textarea
            rows={3}
            value={form.notes}
            placeholder="Anything you want to remember about this brief — instructing context, deadlines mentioned in the brief, etc."
            onChange={(e) => update('notes', e.target.value)}
          />
        </section>

        {/* ───────────────── Actions ───────────────── */}
        <div
          style={{
            marginTop: 22, paddingTop: 16, borderTop: '1px solid var(--color-border)',
            display: 'flex', justifyContent: 'flex-end', gap: 10, alignItems: 'center',
          }}
        >
          {error && <span className="field-help-error" style={{ flex: 1 }}>{error}</span>}
          <button type="button" onClick={() => nav('/matters')} disabled={submitting}>
            Cancel
          </button>
          <button type="submit" className="primary" disabled={!isValid || submitting}>
            {submitting ? 'Creating…' : 'Create matter'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Picker helpers
// ---------------------------------------------------------------------------

function courtPickerLabel(c: Court): string {
  if (c.tier !== 'magistrate_court') return c.name;
  const subtype = c.court_subtype && c.court_subtype !== 'magistrate'
    ? ` · ${c.court_subtype.charAt(0).toUpperCase() + c.court_subtype.slice(1)} Court`
    : '';
  const suffixes: string[] = [];
  if (asBool(c.is_main_seat))         suffixes.push('main seat');
  if (asBool(c.is_equality_court))    suffixes.push('EQC');
  if (asBool(c.is_small_claims_court)) suffixes.push('SCC');
  const tail = suffixes.length ? ` [${suffixes.join(', ')}]` : '';
  return `${c.name}${subtype}${tail}`;
}

function courtGroupLabel(c: Court): string {
  switch (c.tier) {
    case 'constitutional':       return 'Constitutional Court';
    case 'sca':                  return 'Supreme Court of Appeal';
    case 'high':                 return 'High Courts';
    case 'specialised':          return 'Specialised Courts';
    case 'tribunal':             return 'Tribunals';
    case 'small_claims':         return 'Small Claims Courts';
    case 'arbitration':          return 'Arbitration';
    case 'magistrate_court': {
      const prov = c.division || "Magistrates' Courts";
      const dist = c.magisterial_district;
      return dist ? `${prov} → ${dist}` : `${prov} → (no district)`;
    }
    case 'regional_magistrate':
    case 'district_magistrate':  return c.division ? `Magistrates' Courts — ${c.division}` : "Magistrates' Courts";
    default:                     return 'Other';
  }
}
